/**
 * zh-HK（預設語言）—— **基準字典**
 *
 * ⚠️ 本檔係三語嘅「型別真相源」：`src/i18n.ts` 用 `const _parityEn: typeof zhHK = en`
 *    檢查其他語言 → **缺 key／型別唔同 ＝ build（typecheck）即刻紅** ✗
 * ⚠️ 加 key 一定三語齊＋跑 `npm run build`（內含 `check:locales`、`check:wording` 同 `typecheck`）。
 *
 * ⭐ **2026-10-11 產品負責人**：**整個 app 嘅界面語言 ＝ 正式繁體書面中文**
 *   （唔准粵語口語：唔／嘅／喺／撳／搵／咗…）→ 守衛 `npm run check:wording`。
 *   ⚠️ **歌詞**係另一回事（歌詞有自己嘅粵語白名單 `decisions/051`）。
 */
export const zhHK = {
  app_name: 'CoSingery',
  app_tagline: '開心、難過、想記住 —— 都可以為自己留一首歌',

  skeleton_title: '骨架已就緒',
  skeleton_body:
    '目前已有三語介面、設計 token 與建置閘。對話與歌曲生成會按階段閘逐塊加入（安全層優先）。',
  skeleton_gate_note: '發佈前必須通過：建置 ＋ 三語 key 對齊 ＋ 共用方案賬簿一致。',

  language_label: '語言',
  locale_zh_hk: '繁體中文（香港）',
  locale_en: 'English',
  locale_zh_cn: '简体中文',

  nav_chat: '對話',
  nav_songs: '我的歌',

  chat_placeholder: '想說什麼都可以…',
  chat_send: '送出',
  chat_thinking: '請稍等…',
  chat_retry: '再試一次',
  /** chat-thread 1.1.0：引用某條訊息（帶入輸入框） */
  /** 歌詞語言（粵／普／英；`decisions/018`） */
  song_lang_label: '歌詞語言',
  song_lang_cantonese: '粵語',
  song_lang_mandarin: '國語',
  song_lang_english: 'English',
  song_generate_button: '生成歌曲（約 1–3 分鐘）',

  chat_quote: '引用',

  chat_empty_title: '有些時刻，會過去。但那首歌，永遠是你的。',
  chat_empty_body:
    'CoSingery 不是教你寫歌，也不會幫你代筆。它是一個安靜的房間，給你一支筆、一段旋律，讓你把今天的自己 —— 開心、失戀、憤怒、心動、懷念⋯⋯ 好好收藏起來。',
  chat_empty_action: '開始',
  chat_hold_note: '此刻我們繼續談；歌稍後再寫。',
  chat_error_stream: '連線發生問題，你可以再試一次。',
  chat_error_model: '目前無法連接模型，請稍後再試。',

  songs_empty_title: '尚無歌曲',
  songs_empty_body: '談完之後，你的歌會出現在這裡。',
  songs_not_built: '這一頁尚未完成（歌曲生成是下一塊）。',
  /** 個人 library（`decisions/018`）：`{{n}}` ＝ 歌數 */
  songs_library_count: '{{n}} 首歌 —— 播放清單包含全部（跨對話）',
  // ── 新對話／繼續（`decisions/018`）──
  new_chat_button: '＋ 新對話',
  continue_chat_button: '繼續對話',
  new_chat_hint: '開新對話不會刪除你的歌 —— 全部保留在「我的歌」。',
  new_chat_will_start: '歌曲已完成 —— 下一個訊息會開新對話（想留下請按「繼續對話」）。',

  song_generate: '生成一首歌',
  song_generating: '正在寫歌詞…',
  song_ready: '歌曲已完成',
  song_toggle: '查看全部歌詞',
  song_status_label: '狀態',
  song_hold_notice: '我們多談一會再寫歌，好嗎？',
  /** 唔夠料寫歌（**唔係錯誤**）→ 温柔邀請繼續傾（`decisions/015`）；對應後端 `need_more` */
  song_need_more_notice: '想寫一首真正屬於你的歌 —— 請多說幾句那件事給我聽，好嗎？',
  song_error_generic: '目前無法寫歌，請稍後再試。',
  song_error_too_generic: '寫得太空泛 —— 請多說幾句你的事，我想寫你真實的經歷。',
  song_error_repetitive: '歌詞重複太多 —— 請多說幾句，讓我有新的內容可寫。',
  song_error_blocked: '寫出來的內容不合適，我們換個方向再試。',
  // ── 三步流程（`decisions/013`）：① 對話 → ② 歌詞（可改）→ ③ 生成歌曲 ──
  song_step_1: '① 對話',
  song_step_2: '② 歌詞',
  song_step_3: '③ 生成歌曲',
  song_write_lyrics: '寫歌詞',
  lyrics_review_title: '看看是否合適？',
  lyrics_review_hint: '可以自行修改文字與細節 —— 改完就是真正屬於你的歌。',
  lyrics_review_title_label: '歌名',
  lyrics_review_lyrics_label: '歌詞',
  lyrics_review_save: '儲存修改',
  lyrics_review_saved: '已儲存',
  lyrics_review_regenerate: '重新撰寫',
  lyrics_review_confirm: '確定，生成歌曲',
  lyrics_review_confirm_note: '生成歌曲約需 1–3 分鐘（真實音樂生成）。',
  song_render_pending: '正在製作歌曲…（約 1–3 分鐘）',
  song_edit_error_short: '歌詞太短，請多寫幾句。',
  song_edit_error_blocked: '這段歌詞不適合生成歌曲 —— 請換個寫法再試。',
  // ── 版本選擇（Mureka 一次出 2 個版本）──
  variant_label: '版本',
  variant_use: '使用這個',
  variant_picked: '使用中',
  variant_hifi: '高音質',
  /** 1.1.0：按卡即選（唔再另按一粒掣）＋ 每版可以即刻分享 */
  variant_tap_hint: '按一下選擇這個版本；可以逐個試聽、逐個分享',
  /** 2026-09-23：冇音頻就要照實講 */
  song_audio_pending: '音頻仍在生成…（完成後才能播放）',
  song_audio_not_ready: '這首歌尚未有音頻（可能正在生成）—— 請稍後再按 ▶',
  /** 2026-09-23：「用我的聲音唱」逐首歌開／關 */
  voice_mine_on: '使用我的聲音演唱',
  voice_mine_off: '使用預設聲音',
  /** 2026-09-26（`decisions/051`）：演唱性別 */
  gender_auto: '自動',
  gender_male: '男聲',
  gender_female: '女聲',

  // ── Must-use box（選字標記「一定要有」）──
  must_use_hint: '這段文字要加入歌曲：',
  /** 1.2.0（chat-composer）：常駐兩粒掣嘅用法 */
  must_use_howto: '先按上面一粒，再在輸入框選字（要那句入歌）',
  must_use_armed: '已開啟 —— 選字即時標記；再按一次退出',
  must_use_marked_count: '已標記 {{n}} 句會加入歌曲',
  must_use_exact: '要原話（逐字）',
  must_use_meaning: '要意思（可改寫）',
  must_use_remove: '取消標記',
  // ── 播放器 ──
  tab_chat: '對話',
  tab_songs: '我的歌',
  tab_player: '播放器',
  player_now_playing: '正在播放',
  player_play: '播放',
  player_pause: '暫停',
  player_prev: '上一首',
  player_next: '下一首',
  player_seek: '跳至',
  player_hifi_on: '高音質（flac）已開啟',
  player_hifi_off: '一般音質（mp3）',
  player_error: '播放發生錯誤',
  player_empty_title: '尚無歌曲可以聆聽',
  player_empty_body: '先去談幾句，寫一首屬於你的歌。',
  songs_list_failed: '無法讀取我的歌，請稍後再試。',

  /*
   * 情緒應對（`decisions/009` 乙案）
   * 預設＝照樣對話、照樣寫歌；**只有即時危險**才用以下三句（延後寫歌，並非禁止）
   * ⚠️ 唔准「加油」／「我明白你感受」；唔准承諾保密或永遠在場
   */
  send_aria: '送出',
  topbar_new_chat_aria: '新對話',
  topbar_manage_lyrics: '管理歌詞',
  topbar_start_song: '開始寫歌',
  keep_going_toggle: '繼續對話',
  sheet_close_aria: '關閉',
  sheet_start_song_title: '寫一首歌',
  sheet_lyrics_title: '歌詞',
  /** 語體（`decisions/030`）：市場書面（預設）／口語 */
  lyrics_sheet_pick_register: '唱法（想接近哪一種歌）',
  register_cantopop: '流行曲（市場書面）',
  register_spoken: '口語（貼近我原來的說法）',
  register_hint: '市場書面＝以書面語為主、句子短、接近電台歌曲；口語＝保留你原本的說法。',
  lyrics_sheet_pick_lang: '用哪一種語言書寫？',
  lyrics_sheet_edit: '手動編輯',
  lyrics_sheet_cancel: '取消編輯',
  lyrics_sheet_later: '稍後再處理',
  must_use_exact_short: '原話',
  must_use_meaning_short: '意思',
  must_use_howto_short: '按鈕 → 選字',

  understanding_start: '開始（我先複述我聽到的）',
  understanding_ok: '正確，繼續寫',
  understanding_fix: '想修改',
  lyrics_copy: '複製',
  share_button: '分享',
  remake_button: '換風格',
  revise_hint: '說說你想怎樣修改（例：第二段用回我原本的語氣）',
  remake_hint: '想怎樣改風格？（例：慢一點、鋼琴、不要鼓）',
  revise_placeholder: '想改哪一句／怎樣改？',
  remake_placeholder: '想要什麼風格？',
  history_title: '之前的對話',
  history_empty: '尚無其他對話。',
  history_untitled: '（未有訊息）',
  history_songs: '{{n}} 首歌',
  working_hint: '處理中…（請不要離開這一頁）',

  edit_hint: '按你想修改的句子（可多選），再在下面輸入修改內容',
  edit_line_placeholder: '你想這一句變成怎樣？',
  edit_apply_ai: '交由 AI 修改',
  edit_apply_direct: '直接套用',
  edit_done: '完成',
  pick_hint: '在上面訊息按你想加入歌曲的句子（再按一下即取消）',
  style_title: '想要什麼風格？',
  style_start: '開始生成（會收費）',
  /** 1.1.0：快速清單 ＋〔全部風格〕（`decisions/034`） */
  style_quick_label: '快速選擇（你可以增減）',
  style_show_all: '全部風格（{{n}}）',
  style_collapse: '收起',
  style_quick_add: '加入快速清單／移出',
  style_slow: '慢歌',
  style_upbeat: '輕快',
  style_piano: '鋼琴',
  style_guitar: '木吉他',
  style_drums: '鼓',
  style_nodrums: '無鼓',
  style_lofi: 'Lo-fi',
  style_jazz: '爵士',
  style_electronic: '電子',
  style_strings: '弦樂',
  style_rock: '搖滾',
  style_folk: '清新民謠',
  go_player: '前往播放器',
  share_song_text: '這首是我用 CoSingery 寫的歌',
  share_file_fallback_note: '你的瀏覽器不支援直接分享檔案，已改為分享連結',
  /** 2026-09-23（share-button 1.2.0）：分享要「有反應、不會無限等待、失敗要照實講」 */
  share_preparing: '正在準備檔案…',
  share_timeout_note: '檔案取得太慢或失敗 —— 已改為分享文字＋連結（想再試可以再按一次）',
  share_gesture_note: '系統不允許分享檔案（多數是等待太久）—— 已改為分享文字＋連結',
  mode_listener: '聆聽者',
  mode_chat: '聊天',
  mode_switch_aria: '對話模式',
  chat_mode_hint: '這個模式可以談任何事 —— 音樂照樣幫你寫。',
  chat_mode_placeholder: '想說什麼都可以…',
  diary_prev: '上一頁',
  diary_next: '下一頁',
  diary_page_aria: '日記簿',



  diary_done_only: '只收錄已完成（有歌）的日記',
  diary_write_label: '我想記下的感受',
  diary_write_placeholder: '寫下你現在想說的…（自動儲存）',
  diary_saving: '正在儲存…',
  diary_saved: '已儲存',
  diary_save_failed: '無法儲存，可以重試',
  diary_retry: '重試',
  diary_lyrics_title: '這首歌的歌詞',
  diary_no_audio: '這首尚未生成音樂（可以在對話再按〔生成歌曲〕）',
  diary_empty_title: '日記簿尚未開始',
  diary_empty_body: '生成一首歌之後，這裡會出現一頁 —— 有日期、你的感受、歌詞與播放器。',
  history_rename: '改名',
  /** 0016（`decisions/039`）：改歌名（日記頁歌名旁 ✎） */
  song_rename: '改歌名',
  song_rename_title: '為這首歌改名',
  song_rename_placeholder: '例如：尾班車與月光',
  song_rename_empty: '歌名不可以留空',
  history_delete: '刪除',
  history_rename_title: '為這個對話改名',
  history_name_placeholder: '例如：與母親的那件事',
  history_save: '儲存',
  history_cancel: '不要',
  history_delete_title: '確定刪除這個對話？',
  history_delete_warning: '訊息會被刪除，無法復原。（你的歌會保留在「我的歌」。）',
  history_delete_confirm: '刪除對話',
  history_rename_failed: '無法改名',
  history_delete_failed: '無法刪除',
  diary_title: 'My Feeling Diary',
  tab_me: '我的',
  me_identity_title: '身份',
  me_identity_note: '你目前使用「裝置身份」（未登入）—— 換機或清除瀏覽器資料後，我們便無法找回你的歌。登入功能是下一步。',
  me_identity_id: '裝置編號',
  me_billing_title: '付款管理（香港網上付款）',
  me_credits: '可用額度（可以生成多少首歌）',
  me_billing_provider: '收款狀態',
  me_billing_ready: '已接通',
  me_billing_not_ready: '未接通',
  me_billing_no_pricing: '未定價（尚無方案）',
  me_billing_needs: '要開通香港網上收款，需要你（產品負責人）提供：\n① 商戶帳戶／收款方式（FPS 轉數快／信用卡／PayPal／PayMe…）\n② 每首（或每個套裝）的定價（HK$）\n③ 退款政策\n④ provider 的 API key（我們只會存放於 Cloudflare secret，不會寫入程式碼）',
  me_billing_refund_note: '⚠️ 未接通之前，不會有任何收費；接通之後每張訂單都會照實列於上方。',
  me_orders_title: '訂單',
  me_orders_empty: '未有任何訂單',
  /** 0017（`decisions/041`）：登入（Email Magic Link）＋ 免費試用＋額度 */
  login_title: '歡迎來到 CoSingery',
  login_subtitle: '留下 email 即可 —— 第一次送你 1 首歌。',
  login_email_label: '你的 email',
  login_email_placeholder: 'you@example.com',
  login_send_email_only: '用 email 登入',
  login_send_with_invite: '用邀請碼登入',
  login_error_invite_required_hint: '這個 email 尚未登記 —— 請輸入邀請碼再試。',
  login_sending: '正在寄出…',
  login_sent_notice: '已寄至 {{email}} —— 輸入下方 6 位數碼即可登入。',
  login_sent_hint: '找不到？請查看垃圾郵件。（只認最新一封）',
  login_code_label: '6 位數碼',
  login_code_placeholder: '123456',
  login_code_busy: '正在核對…',
  login_need_email_short: '請先輸入 email，再輸入數碼。',
  login_code_error_invalid: '這個數碼不正確，或者已經用過／過期 —— 可以再寄一個新的。',
  login_code_error_network: '網絡有些問題，請再試一次。',
  login_devlink_label: '（本機開發）登入連結：',
  login_error_email_invalid: '這個 email 似乎不正確，請再檢查一次。',
  login_error_send_failed: '目前無法寄出信件，請稍後再試。',
  login_error_too_many: '輸入太頻密，請稍後再試。',
  login_error_link_invalid: '這條登入連結已經用過或已過期 —— 我們再寄一條新的給你。',
  login_error_generic: '登入發生問題',
  login_privacy_note: '不會寄送廣告；email 只用於登入與收據。',
  login_beta_notice: '🧪 測試期：需要邀請碼，每人 1 首免費，暫時未收取任何費用。',
  login_invite_label: '邀請碼',
  login_invite_placeholder: 'INV-XXXX-XXXX-XXXX',
  login_error_invite_required: '這個測試期需要邀請碼 —— 請先輸入邀請碼（如沒有請告訴我）。',
  login_error_invite_invalid: '這個邀請碼不正確、已用過或已過期 —— 請告訴我，我發一個新的給你。',
  login_error_invite_mismatch: '這個邀請碼已綁定另一個 email —— 想用這個 email 就需要一個新邀請碼。',
  login_direct_link_notice: '✅ 邀請碼正確 —— 按下方連結即可登入（15 分鐘內有效，只可使用一次）：',
  feedback_title: '提供意見（Beta）',
  feedback_privacy_note:
    '⚠️ 只會送出你輸入的文字（不會自動附帶歌詞／日記）；只有我（產品負責人）看得到，用於改善下一版。',
  feedback_kind_ux: '體驗',
  feedback_kind_bug: '問題',
  feedback_kind_idea: '想法',
  feedback_kind_other: '其他',
  feedback_score_label: '整體感覺',
  feedback_placeholder: '哪裡卡住？你最想要哪一項？（一兩句即可）',
  feedback_send: '送出',
  feedback_sending: '正在送出…',
  feedback_cancel: '稍後再處理',
  feedback_thanks: '已收到，多謝你 🙏（我會看，並用於改善下一版）',
  feedback_error_empty: '請先寫一句再送出。',
  feedback_error_too_many: '今日已達上限，明天再說也不遲。',
  feedback_error_generic: '目前無法送出',
  me_feedback_button: '提供意見',
  me_feedback_hint: 'Beta 測試期：你的意見直接影響下一版。',
  legal_provider: '服務提供者',
  legal_br: '商業登記號碼（BR）',
  legal_address: '地址',
  legal_email: '聯絡',
  legal_missing: '待提供',
  legal_policies_pending: '（服務條款／退款政策／私隱政策：準備中）',
  legal_load_failed: '無法讀取服務提供者資料',
  pay_payee_label: '收款方',


  me_logged_in_as: '登入帳號',
  me_logout: '登出',
  me_credits_title: '可用額度',
  me_credits_left: '仍可生成 {{n}} 首歌',
  me_credits_none: '已沒有額度 —— 購買一個套餐就可以繼續生成歌曲。',
  me_credits_free_trial: '（其中 1 首為免費試用）',
  me_topup_cta: '購買套餐',
  pack_p10: '10 首歌',
  pack_p25: '25 首歌',
  pack_p75: '75 首歌',
  render_variants_one: '1 個版本',
  render_variants_two: '2 個版本（可以選擇）',
  song_error_insufficient_credits: '已沒有額度 —— 到「我的」購買一個套餐，就可以繼續生成歌曲。',
  song_error_login_required: '請先登入，才可以生成歌曲。',
  me_about_title: '關於',
  /** 2026-09-23：恢復我的歌（清除瀏覽器資料／換機之後找回 library） */
  restore_title: '恢復我的歌',
  restore_warn:
    '⚠️ 目前是「裝置身份」（未登入）：**清除瀏覽器資料／換機**會令這部機的 library 變空（歌曲其實仍然存在），保存下方那個碼就可以找回。',
  restore_code_label: '你的恢復碼',
  restore_no_code: '（無法讀取）',
  restore_copy: '複製恢復碼',
  restore_copied: '已複製 —— 建議貼到自己的記事簿／密碼管理器保存',
  restore_input_placeholder: '貼上舊的恢復碼',
  restore_button: '恢復',
  restore_busy: '正在查詢…',
  restore_invalid: '這個碼格式不正確（應該是一串 16–64 位的英數字／橫線）',
  restore_failed_empty: '這個恢復碼名下**找不到歌**（沒有改動你現在的資料）',
  restore_failed_offline: '目前查不到（網絡問題）—— 請再試（沒有改動你的資料）',
  restore_ok: '已恢復 ✓ 正在載入你的歌…',
  restore_emergency_hint: '⚠️ 如果這一頁看不到「恢復我的歌」（app 未更新），請直接開啟緊急恢復頁：',
  me_build_label: '外殼版本',
  me_build_unknown: '無法讀取',
  me_share_probe_label: '分享能力',
  me_share_probe_legend: '（📄 檔案／🖼 圖片／💬 文字／📱 已安裝 app；✓ 支援／✗ 不支援）',
  restore_card_title: '已安裝到主畫面？／由 Safari 過來？',
  restore_card_body:
    'iPhone 上「主畫面 app」與 Safari 是**分開儲存**（各自身份）→ 在這裡貼上你的恢復碼，歌就會立即回來。',
  restore_ios_note:
    'iPhone：在 Safari 按〔複製恢復碼〕→ 開啟主畫面 app → 在這裡貼上（Apple 不允許連結直接開啟主畫面 app；Android 可以）。',
  share_failed_note: '分享不成功（系統回報原因）',
  share_file_unsupported_note: '這部機不支援「連檔案一齊分享」——改為只分享文字／連結。',
  restore_own_count: '（這部機 {{n}} 首）',
  restore_own_count_unknown: '（查不到）',
  restore_which_code_hint:
    '提示：上方「你的恢復碼」＝ **這部機現在**使用的碼。要找回舊歌，需貼上**另一個**（舊機／Safari 那邊「我的」顯示的碼）。',
  restore_failed_same_code:
    '⚠️ 你貼的就是**這部機現在**的碼（0 首）—— 要貼**舊有歌那個碼**（你之前抄下的那個），不是上方那個。',
  share_page_button: '分享整頁',
  share_page_creating: '正在建立分享連結…',
  share_page_created: '已建立分享連結（{{days}} 日後失效）——可以分享或複製',
  share_page_share: '分享連結',
  share_page_copy: '複製連結',
  share_page_copied: '已複製連結',
  share_page_revoke: '撤回',
  share_page_revoked_ok: '已撤回（該連結立即失效）',
  share_page_includes: '分享頁包括：日期／歌名／播放器／歌詞／你寫的感受（⚠️ 收到連結的人會看到這些內容）',
  share_page_text: '我有一頁 My Feeling Diary 想與你分享：',
  share_page_shared: '已交由系統分享',
  share_page_share_failed: '無法分享（可以試〔複製連結〕）',
  share_page_from: '一位朋友與你分享了這首歌',
  share_page_meta: '已經有 {{views}} 次瀏覽',
  share_page_not_found: '這條分享連結不存在（可能是輸入有誤）',
  share_page_revoked: '這條分享已被撤回 🚫',
  share_page_expired: '這條分享已過期',
  share_page_failed: '目前無法查看這條分享',
  share_page_version: '第 {{n}} 版／共 {{total}} 版',
  me_version: '版本',
  me_load_failed: '無法讀取資料',
  me_refresh: '重新整理',
  me_methods_not_set: '未設定收款碼（產品負責人需要提供 FPS／AlipayHK／WeChat Pay 的收款 QR 圖）。',
  me_manual_pick_pack: '選擇套餐',
  me_manual_pick_method: '選擇付款方式',
  me_manual_create: '建立訂單',
  me_manual_reference: '參考編號',
  me_manual_pay_hint: '請用上方的收款碼付款，並在備註寫下參考編號 —— 我們對帳會快一點。',
  me_manual_pending: '狀態：等待付款',
  me_manual_claimed: '狀態：已收到你的付款通知，等待確認（通常數小時內）',
  me_manual_paid: '狀態：已入帳 ✅（額度已增加）',
  me_manual_claim_button: '我已經付款',
  me_manual_confirm_note: '⚠️ 個人收款碼沒有自動通知 → 我們會人手核對（多數數小時內）；核對完成後才會增加額度。',
  me_owner_queue_title: '等待確認收款',
  me_owner_queue_empty: '沒有訂單等待確認 👍',
  me_owner_confirm_button: '確認已收款（增加額度）',
  me_owner_confirmed: '已確認 ✅',
  me_owner_queue_hint: '只顯示「已付款／待付款」的訂單；確認之後額度會立即加到買家帳號（同一張訂單不會重複加）。',
  /* ── 播放清單／分類（0013；`decisions/033`）── */
  pl_favourite: '我的最愛',
  pl_happy: '開心',
  pl_sad: '難過',
  pl_excited: '興奮',
  pl_celebrate: '慶祝',
  pl_to_my_love: '給我愛的人',
  pl_missing: '思念',
  pl_gratitude: '感恩',
  pl_healing: '療癒',
  /** ⭐ `decisions/052` R-05：原本「加油」→ 改名（產品負責人批准） */
  pl_cheer: '勇氣',
  pl_proud: '驕傲',
  pl_angry: '憤怒／不甘心',
  pl_fun: '有趣／搞笑',
  playlist_add_button: '加入播放清單',
  playlist_sheet_title: '加入到哪一個清單？',
  playlist_sheet_hint: '可以加入多個 —— 分類由你自己選擇，我們**不會**自動幫你分類。',
  playlist_added_note: '已加入「{{name}}」',
  playlist_already_note: '「{{name}}」本身已經有這首歌',
  playlist_removed_note: '已經移出「{{name}}」',
  playlist_not_in_note: '本身不在這個清單',
  playlist_untitled: '未命名清單',
  playlist_membership_failed: '無法讀取你已加入哪些清單',
  playlist_action_failed: '操作失敗（{{error}}）',
  playlist_new_label: '建立新清單',
  playlist_new_placeholder: '例：跑步聽',
  playlist_new_button: '建立',
  playlist_new_failed: '無法建立（{{error}}）',
  playlist_done: '完成',
  playlist_count: '{{n}} 首',
  playlist_all_songs: '全部歌',
  playlist_load_failed: '無法讀取這個清單的歌曲',
  player_play_all: '▶ 播放全部',
  player_diary_label: '你寫的日記',
  player_no_lyrics: '這首歌尚未有歌詞',
  player_deck_aria: '播放器／歌詞（向左翻看歌詞）',
  player_back_to_list: '返回歌單',
  player_repeat_off: '不循環（播放到最後即停止）',
  player_repeat_one: '單曲循環',
  player_repeat_all: '全部循環',
  player_shuffle_on: '隨機播放：開啟',
  player_shuffle_off: '隨機播放：關閉',
  player_fav_add: '加入我的最愛',
  player_fav_remove: '從我的最愛移走',
  player_sequence_note: '一首接一首播放（播放到最後即停止）',
  diary_mode_diary: '日記',
  diary_mode_list: '清單',
  diary_mode_switch_aria: '檢視方式（日記／清單）',
  diary_play_through: '▶ 順序播放',
  diary_page_of: '第 {{n}} 頁 / 共 {{total}} 頁',
  flip_hint_prev: '翻回之前',
  flip_hint_next: '翻到之後',
  diary_too_long: '日記太長（上限 {{max}} 字）—— 可以分成幾篇書寫',
  playlist_empty: '這個清單尚未有歌',
  playlist_empty_hint: '到「我的歌」或對話，按〔加入播放清單〕',
  /* ── 音樂風格（`decisions/034`）── */
  genre_label: '音樂風格',
  genre_auto: '自動（AI 為你選擇）',
  genre_self: '自行選擇',
  genre_auto_note: 'AI 會按你說的內容選擇一個貼近的風格；想自行選擇就按右邊。',
  genre_auto_picked: 'AI 已選擇：{{name}}',
  genre_kpop: '韓國流行（K-POP）',
  genre_cantopop: '粵語流行（HK POP）',
  genre_mandopop: '華語流行',
  genre_ballad: '抒情慢歌',
  genre_piano: '鋼琴版',
  genre_acoustic: '木吉他',
  genre_rock: '搖滾',
  genre_rap: '說唱（Rap）',
  genre_rnb: 'R&B',
  genre_edm: '電子舞曲（EDM）',
  genre_lofi: 'Lo-fi',
  genre_jazz: '爵士',
  genre_citypop: '城市流行',
  genre_dreampop: '夢幻流行',
  genre_indie: '獨立（Indie）',
  genre_folk: '民謠',
  genre_country: '鄉村',
  genre_blues: '藍調',
  genre_soul: '靈魂',
  genre_funk: '放克',
  genre_disco: '迪斯可',
  genre_house: 'House',
  genre_techno: 'Techno',
  genre_trap: 'Trap',
  genre_dnb: 'Drum & Bass',
  genre_reggae: '雷鬼',
  genre_latin: '拉丁',
  genre_bossanova: 'Bossa nova',
  genre_ambient: '氛圍（Ambient）',
  genre_chill: '慢板放鬆（Chill）',
  genre_unplugged: '不插電',
  genre_metal: '金屬',
  genre_punk: '龐克',
  genre_synthwave: '合成波（Synthwave）',
  genre_orchestral: '管弦樂',
  genre_classical: '古典',
  genre_musical: '音樂劇',
  genre_anime: '動漫風',
  genre_chinesestyle: '中國風',
  genre_lullaby: '搖籃曲',
  safety: {
    hold_intro: '我聽到你說的話。此刻我不急著說其他，我想先陪伴你。',
    hold_ask: '你此刻不需要獨自一人 —— 有沒有你可以立即聯絡到的人？（家人、朋友、同事都可以）',
    hold_song_later: '我們繼續談。你想的時候，我再幫你寫一首歌，看看能否幫到你。',
  },

  /* ⭐ 2026-10-11 UI v2 新 key（來源：`design/strings.js` NEW 區） */
  menu_open_aria: '打開選單',
  drawer_aria: '選單',
  nav_playlists: '播放清單',
  nav_settings: '設定',
  settings_title: '設定',
  settings_back_aria: '返回',
  settings_font_size: '字體大小',
  settings_font_size_hint: '標題、歌詞、日記的手寫字，會與按鈕及清單一併放大。',
  font_scale_1: '細',
  font_scale_2: '標準',
  font_scale_3: '大',
  font_scale_4: '特大',
  font_scale_5: '最大',
  font_scale_preview: '今天有些事情，想慢慢說。',
  settings_theme: '外觀',
  theme_light: '日間',
  theme_dark: '夜晚',
  theme_system: '跟隨系統',
  settings_notify: '通知',
  notify_song_ready: '歌曲完成後通知我',
  notify_song_ready_hint: '只會通知這一項，不會催促你回來。',
  settings_account: '帳號與額度',
  settings_privacy: '私隱與匿名',
  privacy_anon_note: '對話、歌詞與日記只屬於你。分享出去的頁面不會顯示你的 email 或帳號。',
  settings_export: '匯出日記',
  export_hint: '將全部日記頁（日期、感受、歌詞）下載成一個檔案。',
  export_button: '匯出',
  gen_stage_aria: '生成進度',
  gen_stage_listen: '聽清楚你說的',
  gen_stage_lyrics: '寫歌詞',
  gen_stage_compose: '作曲與編曲',
  gen_stage_mix: '混音',
  gen_keep_chatting: '你可以繼續對話 —— 完成後會在這裡出現。',
  player_open_aria: '打開播放器',
  player_speed: '速度',
  player_volume: '音量',

  /* ⭐ 2026-10-11（`decisions/053`）：底部 tab bar 3 格 */
  tab_diary: '情緒日記',
  tab_playlists: '播放清單',
};

